import React from 'react';
import {Box, FormControl, InputLabel, MenuItem, Select, Typography} from '@mui/material';
import {
    DEFAULT_SCHOOL_DOCUMENT_AUDIENCE_MODE,
    DEFAULT_SCHOOL_DOCUMENT_NOTIFICATION_MODE,
    SCHOOL_DOCUMENT_AUDIENCE_MODE,
    SCHOOL_DOCUMENT_NOTIFICATION_MODE,
} from './schoolDocumentModes';

/* eslint-disable react/prop-types */

const optionBlock = (title, description) => (
    <Box sx={{whiteSpace: 'normal', py: 0.5, maxWidth: 520}}>
        <Typography component="span" variant="body2" fontWeight={600} display="block">
            {title}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{mt: 0.25}}>
            {description}
        </Typography>
    </Box>
);

const audienceModeSummaryLabel = (value) => {
    const v = Number(value);
    if (v === SCHOOL_DOCUMENT_AUDIENCE_MODE.static) return 'Fixed when you save';
    if (v === SCHOOL_DOCUMENT_AUDIENCE_MODE.dynamic) return 'Keeps up with group membership';
    return '';
};

const notificationModeSummaryLabel = (value) => {
    const v = Number(value);
    if (v === SCHOOL_DOCUMENT_NOTIFICATION_MODE.none) return 'No notifications';
    if (v === SCHOOL_DOCUMENT_NOTIFICATION_MODE.initialOnly) return 'Once, when it first applies';
    if (v === SCHOOL_DOCUMENT_NOTIFICATION_MODE.initialAndNewEligible) {
        return 'When it first applies and when new students become eligible';
    }
    return '';
};

const DocumentAudienceModeFields = ({
    audienceMode = DEFAULT_SCHOOL_DOCUMENT_AUDIENCE_MODE,
    notificationMode = DEFAULT_SCHOOL_DOCUMENT_NOTIFICATION_MODE,
    onAudienceModeChange,
    onNotificationModeChange,
}) => (
    <Box display="flex" flexDirection="column" gap={2}>
        <Typography variant="subtitle1" fontWeight="bold">Who sees this over time</Typography>
        <FormControl fullWidth size="small">
            <InputLabel id="school-doc-audience-mode-label">How the audience is kept up to date</InputLabel>
            <Select
                labelId="school-doc-audience-mode-label"
                label="How the audience is kept up to date"
                value={audienceMode}
                onChange={(e) => onAudienceModeChange(Number(e.target.value))}
                renderValue={(v) => audienceModeSummaryLabel(v)}
                MenuProps={{
                    PaperProps: {sx: {maxWidth: 560}}
                }}
            >
                <MenuItem value={SCHOOL_DOCUMENT_AUDIENCE_MODE.static}>
                    {optionBlock(
                        'Fixed when you save',
                        'Only students who match your group rules at that moment can see the document. Students who join those groups later are not added automatically.'
                    )}
                </MenuItem>
                <MenuItem value={SCHOOL_DOCUMENT_AUDIENCE_MODE.dynamic}>
                    {optionBlock(
                        'Keeps up with group membership',
                        'If a student joins a group you included, they can see the document afterward. If they leave or no longer match your rules, who can see it may change over time.'
                    )}
                </MenuItem>
            </Select>
        </FormControl>

        <Typography variant="subtitle1" fontWeight="bold" sx={{pt: 1}}>
            Alerts to families
        </Typography>
        <FormControl fullWidth size="small">
            <InputLabel id="school-doc-notification-mode-label">When to send notifications</InputLabel>
            <Select
                labelId="school-doc-notification-mode-label"
                label="When to send notifications"
                value={notificationMode}
                onChange={(e) => onNotificationModeChange(Number(e.target.value))}
                renderValue={(v) => notificationModeSummaryLabel(v)}
                MenuProps={{
                    PaperProps: {sx: {maxWidth: 560}}
                }}
            >
                <MenuItem value={SCHOOL_DOCUMENT_NOTIFICATION_MODE.none}>
                    {optionBlock(
                        'No notifications',
                        'Do not send alerts about this document when students become eligible to see it.'
                    )}
                </MenuItem>
                <MenuItem value={SCHOOL_DOCUMENT_NOTIFICATION_MODE.initialOnly}>
                    {optionBlock(
                        'Once, when it first applies',
                        'Send a notification only the first time eligible students can see this document.'
                    )}
                </MenuItem>
                <MenuItem value={SCHOOL_DOCUMENT_NOTIFICATION_MODE.initialAndNewEligible}>
                    {optionBlock(
                        'When it first applies and when new students become eligible',
                        'Send a notification when it is first available to eligible students, and again if additional students later become eligible (for example after joining a group).'
                    )}
                </MenuItem>
            </Select>
        </FormControl>
    </Box>
);

export default DocumentAudienceModeFields;
